/**
 * React glue for `@/lib/clipboardImage`.
 *
 * Gives a surface two ways to receive a clipboard image, both routed
 * through the same validation (size guard + type conversion):
 *
 *   * `enabled` registers a paste handler on the shared paste stack,
 *     so Cmd/Ctrl+V anywhere on the page delivers the image — unless
 *     the paste landed in a text field or the body editor, which keep
 *     their own paste behaviour.
 *   * `pasteFromClipboard()` reads the clipboard directly, for the
 *     "Paste image" button. This is the path that works on iPad,
 *     where a keyboard paste with nothing focused is unreliable.
 *
 * Errors come back through `onError(message)` as finished sentences —
 * callers hand them straight to `toast.error`.
 */
import { useCallback, useEffect, useRef } from "react";
import {
  IMAGE_ACCEPT_ANY,
  checkPastedSize,
  clipboardReadSupported,
  coerceImageType,
  imageFileFromClipboardEvent,
  isEditableTarget,
  pushPasteTarget,
  readImageFromClipboard,
} from "@/lib/clipboardImage";

export function usePasteImage({
  onImage,
  onError,
  accept = IMAGE_ACCEPT_ANY,
  enabled = true,
  maxBytes,
} = {}) {
  // Keep the latest callbacks in a ref so changing them doesn't tear
  // down and re-register the paste handler (which would reorder the
  // stack and let a background page steal a modal's paste).
  const latest = useRef({ onImage, onError, accept, maxBytes });
  latest.current = { onImage, onError, accept, maxBytes };

  const handleFile = useCallback(
    async (file) => {
      const { onImage: handle, onError: fail, accept: acc, maxBytes: max } =
        latest.current;
      try {
        checkPastedSize(file, max || undefined);
        const out = await coerceImageType(file, acc);
        handle?.(out);
      } catch (e) {
        fail?.(e?.message || "Could not use the pasted image");
      }
    },
    [],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    return pushPasteTarget((event) => {
      if (isEditableTarget(event.target)) return;
      const file = imageFileFromClipboardEvent(event);
      if (!file) return;
      event.preventDefault();
      handleFile(file);
    });
  }, [enabled, handleFile]);

  const pasteFromClipboard = useCallback(async () => {
    try {
      const file = await readImageFromClipboard();
      await handleFile(file);
    } catch (e) {
      latest.current.onError?.(
        e?.message || "Could not read an image from the clipboard",
      );
    }
  }, [handleFile]);

  return { pasteFromClipboard, canReadClipboard: clipboardReadSupported() };
}

export default usePasteImage;